/**
 * Admin coach view for The Me I See™.
 * Route: /admin/coaching/me-i-see  (require_admin)
 *
 * Lists all clients with progress. Clicking one opens full detail:
 * worksheet answers, journal entries, activity responses, AI feedback,
 * and inline coach-override + private note editors.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, User, ChevronRight, Plus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token");
  return { Authorization: `Bearer ${t}`, "Content-Type": "application/json" };
}

export default function AdminMeISee() {
  const [clients, setClients] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [tuitionPaid, setTuitionPaid] = useState(997);
  const [err, setErr] = useState("");

  async function loadClients() {
    setErr("");
    try {
      const r = await fetch(`${API}/api/coaching/admin/me-i-see/clients`, { headers: authHeaders() });
      if (!r.ok) throw new Error(String(r.status));
      const body = await r.json();
      setClients(body.items || []);
    } catch (e) {
      setErr("Could not load enrolled clients. " + String(e.message));
      setClients([]);
    }
  }
  useEffect(() => { loadClients(); }, []);

  async function openClient(email) {
    setSelected(email);
    setDetail(null);
    const r = await fetch(`${API}/api/coaching/admin/me-i-see/clients/${encodeURIComponent(email)}`, { headers: authHeaders() });
    if (r.ok) setDetail(await r.json());
  }

  async function createEnrollment() {
    setErr("");
    const r = await fetch(`${API}/api/coaching/admin/enrollments`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        client_email: newEmail.trim().toLowerCase(),
        client_name: newName.trim(),
        tuition_paid_usd: Number(tuitionPaid),
        payment_method: "manual_admin",
      }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      setErr(body.detail?.code || `Create failed (${r.status})`);
      return;
    }
    setCreating(false); setNewEmail(""); setNewName(""); setTuitionPaid(997);
    loadClients();
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-me-i-see">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#5B2C83", fontWeight: 600 }}>
              Coaching · Admin
            </div>
            <h1 className="text-3xl mt-2" style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
              The Me I See™ · Enrolled Clients
            </h1>
          </div>
          <button
            onClick={() => setCreating(true)}
            data-testid="new-enrollment-btn"
            className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-2"
            style={{ background: "#5B2C83" }}
          >
            <Plus className="w-4 h-4" /> New enrollment
          </button>
        </div>

        {err && <div className="mb-4 text-sm text-red-700" data-testid="admin-error">{err}</div>}

        {creating && (
          <div className="rounded-2xl bg-white border p-5 mb-6 max-w-2xl" style={{ borderColor: "#eee" }} data-testid="new-enrollment-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Create enrollment</h3>
              <button onClick={() => setCreating(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Client email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#ddd" }} data-testid="new-enrollment-email" />
              <input placeholder="Client name" value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#ddd" }} data-testid="new-enrollment-name" />
              <input placeholder="Tuition paid (USD)" type="number" value={tuitionPaid} onChange={(e) => setTuitionPaid(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#ddd" }} data-testid="new-enrollment-tuition" />
              <button onClick={createEnrollment} className="rounded-full px-4 py-2 text-sm font-medium text-white" style={{ background: "#5B2C83" }} data-testid="new-enrollment-submit">
                Enroll client
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-[380px_1fr] gap-6">
          <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "#eee" }} data-testid="admin-client-list">
            <div className="px-4 py-3 border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500 flex justify-between">
              <span>{clients?.length || 0} enrolled</span>
              <span>Email · Progress</span>
            </div>
            {clients === null ? (
              <div className="p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
            ) : clients.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No clients enrolled yet.</div>
            ) : clients.map((c) => (
              <button
                key={c.enrollment_id}
                onClick={() => openClient(c.client_email)}
                data-testid={`admin-client-row-${c.client_email}`}
                className="w-full text-left px-4 py-3 border-b hover:bg-slate-50 flex items-center gap-3 transition"
                style={{ background: selected === c.client_email ? "#f1eaf6" : "white" }}
              >
                <User className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{c.client_name || c.client_email}</div>
                  <div className="text-xs text-slate-500 truncate">{c.client_email}</div>
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-1">
                  <EmailStatusPill welcome={c.welcome_email} />
                  <div className="text-xs font-medium" style={{ color: "#5B2C83" }}>{c.completed_count}/8</div>
                  <div className="text-[10px] text-slate-500">Wk {c.current_module}</div>
                </div>
              </button>
            ))}
          </div>

          <div>
            {detail ? <ClientDetail detail={detail} onRefresh={() => openClient(selected)} /> : (
              <div className="rounded-2xl bg-white border p-10 text-sm text-slate-400 text-center" style={{ borderColor: "#eee" }}>
                Select a client to view their program journey.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientDetail({ detail, onRefresh }) {
  const [openMod, setOpenMod] = useState(null);
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [welcomeSending, setWelcomeSending] = useState(false);

  async function resendWelcome() {
    setWelcomeSending(true); setWelcomeMsg("");
    try {
      const r = await fetch(`${API}/api/coaching/admin/welcome-email/send`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          client_email: detail.enrollment.client_email,
          program_slug: detail.enrollment.program_slug,
        }),
      });
      const b = await r.json();
      if (b.ok && b.welcome_email?.sent) setWelcomeMsg("Welcome email sent ✓");
      else setWelcomeMsg("Send did not complete: " + (b.welcome_email?.reason || "unknown"));
    } catch (e) { setWelcomeMsg("Send failed: " + e.message); }
    setWelcomeSending(false);
    setTimeout(() => setWelcomeMsg(""), 6000);
  }

  return (
    <div className="rounded-2xl bg-white border p-6" style={{ borderColor: "#eee" }} data-testid="admin-client-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            {detail.enrollment.client_name || detail.enrollment.client_email}
          </div>
          <div className="text-xs text-slate-500">{detail.enrollment.client_email}</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold" style={{ color: "#5B2C83" }}>{detail.progress.progress_pct}%</div>
          <div className="text-[10px] text-slate-500">{detail.progress.completed_count} / 8 modules</div>
        </div>
      </div>
      <div className="mb-4 flex items-center gap-3">
        <button onClick={resendWelcome} disabled={welcomeSending} data-testid="resend-welcome-email-btn"
                className="rounded-full px-3 py-1.5 text-xs border font-medium disabled:opacity-40"
                style={{ borderColor: "#5B2C83", color: "#5B2C83" }}>
          {welcomeSending ? "Sending…" : "Resend welcome email"}
        </button>
        {welcomeMsg && <span className="text-xs" style={{ color: welcomeMsg.includes("✓") ? "#7FA66A" : "#a04040" }} data-testid="welcome-email-status">{welcomeMsg}</span>}
      </div>
      <div className="space-y-2">
        {detail.progress.modules.map((m) => (
          <div key={m.module_number} className="border rounded-xl overflow-hidden" style={{ borderColor: "#eee" }}>
            <button
              onClick={() => setOpenMod(openMod === m.module_number ? null : m.module_number)}
              data-testid={`admin-module-toggle-${m.module_number}`}
              className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium" style={{
                  background: m.completion_status ? "#7FA66A" : m.unlocked ? "#f1eaf6" : "#f6f4f0",
                  color: m.completion_status ? "#fff" : m.unlocked ? "#5B2C83" : "#999",
                }}>
                  {m.completion_status ? "✓" : m.module_number}
                </span>
                <span className="text-sm font-medium">Module {m.module_number}</span>
                {m.coach_override_feedback && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: "#f1eaf6", color: "#5B2C83" }}>coach override</span>
                )}
              </div>
              <ChevronRight className={`w-4 h-4 transition ${openMod === m.module_number ? "rotate-90" : ""}`} />
            </button>
            {openMod === m.module_number && (
              <div className="px-4 py-4 border-t bg-slate-50 space-y-4" style={{ borderColor: "#eee" }}>
                <Section title="Worksheet answers">
                  {(m.worksheet_answers || []).map((a, i) => (
                    <div key={i} className="text-sm text-slate-700"><span className="text-slate-400 text-xs">Q{i + 1}:</span> {a || <span className="text-slate-300 italic">not answered</span>}</div>
                  ))}
                </Section>
                <Section title="Journal entries">
                  {(m.journal_answers || []).map((a, i) => (
                    <div key={i} className="text-sm text-slate-700"><span className="text-slate-400 text-xs">Day {i + 1}:</span> {a || <span className="text-slate-300 italic">not written</span>}</div>
                  ))}
                </Section>
                <Section title="Activity response">
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.activity_response || <span className="text-slate-300 italic">not submitted</span>}</div>
                </Section>
                {m.ai_feedback && (
                  <Section title="AI coaching reflection">
                    <FeedbackReadOnly f={m.ai_feedback} />
                  </Section>
                )}
                <CoachTools email={detail.enrollment.client_email} moduleN={m.module_number} initial={m} onSaved={onRefresh} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#5B2C83" }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function FeedbackReadOnly({ f }) {
  return (
    <div className="rounded-xl bg-white p-4 border" style={{ borderColor: "#eee" }}>
      {[["Reflection", f.reflection], ["Observation", f.observation], ["Reframe", f.reframe], ["Encouragement", f.encouragement]].map(([l, v]) => (
        <div key={l} className="mb-2"><span className="text-[10px] uppercase text-slate-500">{l}:</span> <span className="text-sm text-slate-700">{v}</span></div>
      ))}
      <div><span className="text-[10px] uppercase" style={{ color: "#7FA66A" }}>Next Action:</span> <span className="text-sm text-slate-700">{f.next_action}</span></div>
    </div>
  );
}

function CoachTools({ email, moduleN, initial, onSaved }) {
  const [note, setNote] = useState(initial.coach_note || "");
  const [override, setOverride] = useState(initial.coach_override_feedback || null);
  const [showOverride, setShowOverride] = useState(!!initial.coach_override_feedback);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function save() {
    setSaving(true); setErr("");
    const body = { coach_note: note };
    if (showOverride && override) body.coach_override_feedback = override;
    const r = await fetch(`${API}/api/coaching/admin/me-i-see/clients/${encodeURIComponent(email)}/module/${moduleN}/coach-note`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify(body),
    });
    if (!r.ok) setErr("Save failed");
    setSaving(false);
    onSaved && onSaved();
  }

  return (
    <div className="rounded-xl bg-white p-4 border" style={{ borderColor: "#eee" }} data-testid={`coach-tools-${moduleN}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#5B2C83" }}>Coach tools</div>
      <label className="block text-xs text-slate-600 mb-1">Private note (only visible to admins)</label>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full min-h-[60px] rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#ddd" }} data-testid={`coach-note-input-${moduleN}`} />
      <label className="flex items-center gap-2 mt-3 text-xs text-slate-600">
        <input type="checkbox" checked={showOverride} onChange={(e) => { setShowOverride(e.target.checked); if (e.target.checked && !override) setOverride({ reflection: "", observation: "", reframe: "", encouragement: "", next_action: "" }); }} data-testid={`coach-override-toggle-${moduleN}`} />
        Override the AI feedback with my own 5-part reflection
      </label>
      {showOverride && override && (
        <div className="mt-2 grid gap-2">
          {["reflection", "observation", "reframe", "encouragement", "next_action"].map((k) => (
            <textarea key={k} value={override[k]} onChange={(e) => setOverride({ ...override, [k]: e.target.value })} placeholder={k.replace("_", " ").toUpperCase()} className="w-full min-h-[50px] rounded-lg border px-3 py-2 text-xs" style={{ borderColor: "#ddd" }} data-testid={`coach-override-${k}-${moduleN}`} />
          ))}
        </div>
      )}
      {err && <div className="text-xs text-red-700 mt-2">{err}</div>}
      <button onClick={save} disabled={saving} className="mt-3 rounded-full px-4 py-2 text-xs font-medium text-white inline-flex items-center gap-1" style={{ background: "#5B2C83" }} data-testid={`coach-save-${moduleN}`}>
        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// Compact delivery-status pill for the client list.
// email_logs.status values used by send_kcis_email:
//   - "sent"        → we handed to Resend; treat as delivered until proven otherwise
//   - "delivered"   → confirmed by Resend webhook (if wired)
//   - "opened"      → confirmed opened (if webhook wired)
//   - "bounced"     → hard/soft bounce reported by Resend
//   - "error"       → send-side failure (server-side exception)
//   - "skipped"     → not sent by design (e.g. no key in dev)
function EmailStatusPill({ welcome }) {
  if (!welcome) {
    return <span data-testid="welcome-status-none" className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#eee", color: "#888" }}>no email</span>;
  }
  const s = String(welcome.status || "").toLowerCase();
  const map = {
    delivered: { c: "#7FA66A", bg: "#e9f2e2", label: "delivered" },
    opened:    { c: "#5B2C83", bg: "#f1eaf6", label: "opened" },
    sent:      { c: "#7FA66A", bg: "#e9f2e2", label: "sent" },
    bounced:   { c: "#a04040", bg: "#f8e5e5", label: "bounced" },
    error:     { c: "#a04040", bg: "#f8e5e5", label: "error" },
    failed:    { c: "#a04040", bg: "#f8e5e5", label: "failed" },
    skipped:   { c: "#888",    bg: "#eee",    label: "skipped" },
  };
  const cfg = map[s] || { c: "#888", bg: "#eee", label: s || "unknown" };
  const title = welcome.error ? `${cfg.label} — ${welcome.error}` : cfg.label;
  return (
    <span
      data-testid={`welcome-status-${s || "unknown"}`}
      title={title}
      className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
      style={{ background: cfg.bg, color: cfg.c }}
    >
      {cfg.label}
    </span>
  );
}
